/**
 * RecentChangesPanel — queue #658 §UAT-2026-07-08-R658 §R658-2①.
 *
 * 카탈로그 상단 '최근 변경' 토글로 열리는 인라인 패널(모달 아님). 전 모듈 commits[] 병합 ·
 * 일자 내림차순 · 상위 50 목록. 각 행 = 일자 · 모듈(artifact) · subject · 모듈 노드 딥링크
 * (기존 hash 오토셀렉트 관례 재사용 — `/showcase#<code>`, 노드 매핑 불가 시
 * `/showcase?q=<artifact>` 검색 필터 폴백). 병합/정렬 로직은 `recent-changes.ts` 유틸에
 * 위임(본 컴포넌트는 렌더만).
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ModuleMaturity, ModuleNode } from '../types/showcase';
import { mergeModuleChanges, topRecentChanges, artifactToFirstCode } from '../data/recent-changes';

interface Props {
  maturity: ModuleMaturity;
  nodes: ModuleNode[];
  onClose?: () => void;
}

export function RecentChangesPanel({ maturity, nodes, onClose }: Props) {
  const { t } = useTranslation();
  const entries = topRecentChanges(mergeModuleChanges(maturity), 50);
  const firstCodeMap = artifactToFirstCode(nodes);

  return (
    <section
      aria-label={t('showcase.recentChanges.title')}
      style={{
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface-background)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--spacing-3) var(--spacing-4)',
          borderBottom: '1px solid var(--color-surface-border)',
          background: 'var(--color-surface-elevated)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
          }}
        >
          {t('showcase.recentChanges.title')}
        </h2>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('showcase.panel.docsModal.close')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 16,
              color: 'var(--color-text-muted)',
              lineHeight: 1,
              padding: 'var(--spacing-1)',
            }}
          >
            ✕
          </button>
        )}
      </div>

      {entries.length === 0 ? (
        <p
          style={{
            margin: 0,
            padding: 'var(--spacing-4)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
            fontStyle: 'italic',
          }}
        >
          {t('showcase.recentChanges.empty')}
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            maxHeight: 360,
            overflowY: 'auto',
          }}
        >
          {entries.map((entry, idx) => {
            const code = firstCodeMap.get(entry.artifact);
            const to = code ? `/showcase#${code}` : `/showcase?q=${encodeURIComponent(entry.artifact)}`;
            return (
              <li
                key={`${entry.date}-${entry.artifact}-${idx}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-3)',
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  borderBottom: '1px solid var(--color-surface-border)',
                  fontSize: 'var(--font-size-xs)',
                }}
              >
                <code
                  style={{
                    fontFamily: 'var(--font-family-mono)',
                    color: 'var(--color-text-muted)',
                    flexShrink: 0,
                    minWidth: 76,
                  }}
                >
                  {entry.date}
                </code>
                <span
                  style={{
                    fontFamily: 'var(--font-family-mono)',
                    color: 'var(--color-brand-primary)',
                    flexShrink: 0,
                  }}
                >
                  {entry.artifact}
                </span>
                <span
                  title={entry.subject}
                  style={{
                    flex: 1,
                    color: 'var(--color-text-secondary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {entry.subject}
                </span>
                <Link
                  to={to}
                  aria-label={t('showcase.recentChanges.rowAria', { date: entry.date, artifact: entry.artifact })}
                  style={{
                    flexShrink: 0,
                    color: 'var(--color-brand-primary)',
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  →
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
